import mapboxgl from 'mapbox-gl';
import { atMapsToGeojson } from '@alltrails/maps/utils/legacyGeoJSONConversions';
import { defineMessages } from '@alltrails/shared/react-intl';
import { TrailPlanner } from '../../../../../utils/trail_planner/trail_planner';
import { putAtMapIntoBackground, bringAtMapIntoForeground, makeAtMapVisible, makeAtMapInvisible } from '../../../../../utils/mapbox/overlays/at_map';
import { addDistanceMarkers } from '../../../../../utils/mapbox/overlays/distance_markers';
import { mountReactComponent, getPopupDOMContent, unmountReactComponent } from '../../../../../utils/mapbox/event_handling/popup_helpers';
import SearchPointPopup from '../../../../../components/MapCreator/SearchPointPopup';
import CustomProvider from 'components/CustomProvider';

import { updateLayerProperty, removeLayer } from '../../../../../utils/mapbox/layers/layer_helpers';
import {
  addTrailPlannerOverlay,
  removeTrailPlannerMap,
  removeInsertModeMap,
  routeNodesId,
  routePolylinesId,
  routeChevronsId,
  routeNodesDragId,
  newRouteNodeId
} from '../../../../../utils/mapbox/overlays/trail_planner_overlay';
import { setWindowExitGuard } from '../../../../../utils/window_helpers';

const MESSAGES = defineMessages({
  dragToChange: { defaultMessage: "Drag to change route, 'delete' to remove" },
  clickToAddPoint: { defaultMessage: 'Click to add point' },
  outSelected: { defaultMessage: 'Out Selected' },
  backSelected: { defaultMessage: 'Back Selected' },
  allSelected: { defaultMessage: 'All Selected' }
});

const TrailPlannerMixin = {
  trailPlannerMessagingSubscriptions() {
    if (this.listeners == null) this.listeners = [];
    this.listeners.push(this.props.messagingChannel.subscribe('button.click.undo', this.undoClick));
    this.listeners.push(this.props.messagingChannel.subscribe('button.click.redo', this.redoClick));
    this.listeners.push(this.props.messagingChannel.subscribe('button.click.out_and_back', this.outAndBackClick));
    this.listeners.push(this.props.messagingChannel.subscribe('button.click.return', this.returnClick));
    this.listeners.push(this.props.messagingChannel.subscribe('button.click.reverse', this.reverseClick));
    this.listeners.push(this.props.messagingChannel.subscribe('map.click', this.mapClick));
    this.listeners.push(this.props.messagingChannel.subscribe('map.shift_drag.start', this.mapShiftDragStart));
    this.listeners.push(this.props.messagingChannel.subscribe('map.shift_drag.end', this.mapShiftDragEnd));
    this.listeners.push(this.props.messagingChannel.subscribe('map.shift_drag.move', this.mapShiftDragMove));
    this.listeners.push(this.props.messagingChannel.subscribe('map.ctrl_key', this.mapCtrlKey));
    this.listeners.push(this.props.messagingChannel.subscribe('map.del_key', this.mapDelKey));
    this.listeners.push(this.props.messagingChannel.subscribe('map.esc_key', this.mapEscKey));
    this.listeners.push(this.props.messagingChannel.subscribe('route.edit.changeColor', this.handleRouteColorChange));
    this.listeners.push(this.props.messagingChannel.subscribe('edit.enable_trail_planner', this.enablePlanning));
    this.listeners.push(this.props.messagingChannel.subscribe('edit.enable_insert_mode', this.enableInsertMode));
    this.listeners.push(this.props.messagingChannel.subscribe('edit.disable_insert_mode', this.disableInsertMode));
    this.listeners.push(this.props.messagingChannel.subscribe('map.enter_return_key', this.applyInsertRouteSection));
    this.listeners.push(this.props.messagingChannel.subscribe('edit.disable_trail_planner', this.disablePlanning));
    this.listeners.push(this.props.messagingChannel.subscribe('edit.show_popup_at_lat_lng', this.showPopupAtLatLng));
  },
  showPopupAtLatLng(data) {
    const [lat, lng, placeName, zoom = 15] = data;
    const partialPlaceName = placeName.split(',')[0];
    const isStartingPoint = !this.state.trailPlanner.routeStart;
    const popupOptions = { closeButton: false, closeOnClick: false, offset: 15 };

    // different zoom behavior based on whether or not route is starting
    if (isStartingPoint) {
      this.setMapCenterAndZoom(lat, lng, zoom);
    } else {
      const bounds = this.mbMap.getBounds();
      this.mbMap.fitBounds(bounds.extend([lng, lat]), { padding: 200 });
    }

    const markerDot = document.createElement('div');
    markerDot.classList.add(isStartingPoint ? 'mapboxgl-green-dot-marker' : 'mapboxgl-gray-dot-marker');

    // create new marker
    const searchPointMarker = new mapboxgl.Marker({
      draggable: true,
      element: markerDot
    });

    const removeMarkerAndUnmountPopup = (marker, domContent) => {
      unmountReactComponent(domContent);
      marker.remove();
    };

    const addPointAndRemoveMarkerAndPopup = (coordinates, marker, domContent) => {
      const [longitude, latitude] = coordinates;
      this.state.trailPlanner.routeToPoint([longitude, latitude]);

      removeMarkerAndUnmountPopup(marker, domContent);
    };

    const DOMContent = mountReactComponent(
      <CustomProvider>
        <SearchPointPopup
          id="search-popup"
          onClose={() => removeMarkerAndUnmountPopup(searchPointMarker, DOMContent)}
          isStartingPoint={isStartingPoint}
          locationName={partialPlaceName}
          addToRoute={() => addPointAndRemoveMarkerAndPopup([lng, lat], searchPointMarker, DOMContent)}
        />
      </CustomProvider>
    );

    // create popup
    const initialPopup = new mapboxgl.Popup(popupOptions).setDOMContent(DOMContent);
    // add popup to marker
    searchPointMarker
      .setLngLat([lng, lat])
      .setPopup(initialPopup)
      .addTo(this.mbMap)
      .togglePopup();

    searchPointMarker.on('dragend', () => {
      // unmount current popup component
      const currentPopupContent = getPopupDOMContent(searchPointMarker.getPopup());
      unmountReactComponent(currentPopupContent);

      const coordinates = searchPointMarker.getLngLat();

      // update the domContent
      const updatedDOMContent = mountReactComponent(
        <CustomProvider>
          <SearchPointPopup
            onClose={() => removeMarkerAndUnmountPopup(searchPointMarker, updatedDOMContent)}
            isStartingPoint={isStartingPoint}
            addToRoute={() => addPointAndRemoveMarkerAndPopup([coordinates.lng, coordinates.lat], searchPointMarker, updatedDOMContent)}
          />
        </CustomProvider>
      );
      // update popup with new domContent
      initialPopup.setLngLat([coordinates.lng, coordinates.lat]).setDOMContent(updatedDOMContent);
    });
  },
  enablePlanning(route) {
    if (this.state.trailPlanner) {
      return;
    }

    setWindowExitGuard(true);

    // Update map's default cursor to 'crosshair'
    this.mbMap.customStyle.cursor = 'crosshair';
    this.mbMap.getCanvas().style.cursor = this.mbMap.customStyle.cursor;

    putAtMapIntoBackground(this.mbMap);

    const { updateProgressDialog } = this.props;
    const { routingActive, routingMode } = this.state;
    const onRouteInfoChange = this.onRouteInfoChange;
    // Attach to state immediately to prevent any race condition issues
    this.state.trailPlanner = new TrailPlanner({
      onRouteInfoChange,
      route,
      routingActive,
      routingMode,
      updateProgressDialog
    });
    this.state.trailPlanner.initRoutePolylines();
    this.setState({
      trailPlanner: this.state.trailPlanner,
      currentlyEditingRoute: route,
      selectedRouteNode: null
    });
  },
  disablePlanning() {
    if (!this.state.trailPlanner) {
      return;
    }

    // Restore map's default cursor to ''
    this.mbMap.customStyle.cursor = '';
    this.mbMap.getCanvas().style.cursor = this.mbMap.customStyle.cursor;

    removeTrailPlannerMap(this.mbMap);
    bringAtMapIntoForeground(this.mbMap);
    this.updateDistanceMarkers(this.props.exploreMap);

    setWindowExitGuard(false);
    $('#flash-msg').slideUp();

    this.props.handleRouteInfoChanged(null);
    this.setState({
      trailPlanner: null,
      currentlyEditingRoute: null,
      selectedRouteNode: null
    });
  },
  enableInsertMode() {
    if (this.state.insertTrailPlanner || !this.state.currentlyEditingRoute || !this.state.selectedRouteNode) {
      return;
    }
    
    makeAtMapInvisible(this.mbMap)
    updateLayerProperty(this.mbMap, routePolylinesId, 'paint', 'line-opacity', 0.4)
    updateLayerProperty(this.mbMap, routeChevronsId, 'paint', 'icon-opacity', 0.4)

    const { updateProgressDialog } = this.props;
    const { routingActive, routingMode } = this.state;
    const onInsertRouteInfoChange = this.onInsertRouteInfoChange;
    // Attach to state immediately to prevent any race condition issues
    this.state.insertTrailPlanner = new TrailPlanner({
      onRouteInfoChange: onInsertRouteInfoChange,
      route: undefined,
      routingActive,
      routingMode,
      updateProgressDialog,
      isInsertTrailPlanner: true,
      routeStart: this.state.selectedRouteNode.lngLat
    });

    this.state.insertTrailPlanner.initRoutePolylines();
    this.setState({
      insertTrailPlanner: this.state.insertTrailPlanner,
      selectedRouteNode: null
    });
  },
  applyInsertRouteSection() {
    if (!this.state.insertTrailPlanner) {
      return;
    }
    if (this.state.insertTrailPlanner.endsCoincide) {
      this.state.trailPlanner.mergeRoutes(this.state.insertTrailPlanner)
      this.disableInsertMode();
    } else {
      alert('Start and end point must coincide before you can insert the new route segment')
    }
  },
  disableInsertMode() {
    if (!this.state.insertTrailPlanner) {
      return;
    }

    makeAtMapVisible(this.mbMap)
    updateLayerProperty(this.mbMap, routePolylinesId, 'paint', 'line-opacity', 1)
    updateLayerProperty(this.mbMap, routeChevronsId, 'paint', 'icon-opacity', 1)

    removeInsertModeMap(this.mbMap);
    this.props.handleInsertRouteInfoChanged(null);
    this.setState({
      insertTrailPlanner: null,
      selectedRouteNode: null
    });
    this.state.trailPlanner.checkRoute()
  },
  handleRouteColorChange(newColor) {
    this.state.trailPlanner.color = newColor;
    // Update drawn route
    this.drawTrailPlannerRoute();
    // Update plannerMap at parent level
    const plannerMap = this.state.trailPlanner.toAtMap();
    this.props.handleRouteInfoChanged({ plannerMap });
  },
  onRouteInfoChange(routeInfo) {
    this.drawTrailPlannerRoute();
    this.updateDistanceMarkers(routeInfo.plannerMap);
    if (routeInfo.fullChange) {
      this.props.handleRouteInfoChanged(routeInfo);
      this.setState({
        plannerMap: routeInfo.plannerMap,
        selectedRouteNode: null
      });
    }
    if (this.props.hasAdminFeatures) {
      // sets last placed marker node to selected node
      const selectedNode = this.state.trailPlanner?.lastAddedRouteNode;
      if (selectedNode) {
        this.toggleSelectRouteNode(selectedNode);
        this.state.trailPlanner.lastAddedRouteNode = null;
      }
    }
  },
  onInsertRouteInfoChange(routeInfo) {
    this.drawTrailPlannerRoute(this.state.insertTrailPlanner);
    this.props.handleInsertRouteInfoChanged(routeInfo);
  },
  drawTrailPlannerRoute(trailPlanner = this.state.trailPlanner) {
    const messages = {
      dragToChange: this.props.intl.formatMessage(MESSAGES.dragToChange),
      clickToAddPoint: this.props.intl.formatMessage(MESSAGES.clickToAddPoint),
      backSelected: this.props.intl.formatMessage(MESSAGES.backSelected),
      outSelected: this.props.intl.formatMessage(MESSAGES.outSelected),
      allSelected: this.props.intl.formatMessage(MESSAGES.allSelected)
    }
    // admin features are new route drawing features like marker tooltips, marker toolbar, cut/split, etc
    addTrailPlannerOverlay(this.mbMap, trailPlanner, this.state.trailPlannerColor, messages, this.props.hasAdminFeatures);
  },
  updateDistanceMarkers(atMap) {
    if (this.state.enabledOverlays.includes('distanceMarkers')) {
      addDistanceMarkers(this.mbMap, atMapsToGeojson([atMap]));
    }
  },
  routeNodeClick(feature) {
    const routeNode = feature.properties;
    removeLayer(this.mbMap, `${routeNodesDragId}-labels`);
    if (feature.id === newRouteNodeId) {
      // Split route at new route node
      // these options are for marker nodes placed on route segments that overlap
      const opts = {
        forceSplitOnTopLine: this.props.hasAdminFeatures && !this.state.activeKeyModifier,
        forceSplitOnBottomLine: this.props.hasAdminFeatures && this.state.activeKeyModifier === 'v',
        forceSplitOnBothLines: this.props.hasAdminFeatures && this.state.activeKeyModifier === 'a',
      }
      const trailPlanner = this.state.insertTrailPlanner ? this.state.insertTrailPlanner : this.state.trailPlanner;
      trailPlanner.addNewRouteNode({ ...routeNode, lngLat: feature.geometry.coordinates }, opts);
    } else {
      // Toggle selection of route node
      // TODO find a better way to handle mapbox features converting arrays to strings
      this.toggleSelectRouteNode({...routeNode, lngLat: routeNode.lngLat.slice(1,-1).split(',').map(n => parseFloat(n))});
    }
  },
  mapClick(e) {
    if (this.state.trailPlanner) {
      if (!this.mbMap.shiftKeyDown) {
        // Handle clicking on specific trail planner layers (like a routeNode)
        const features = this.mbMap.queryRenderedFeatures(e.point);
        const feature = features[0];
        if (feature && [routeNodesId, routeNodesDragId].includes(feature.layer.id)) {
          this.routeNodeClick(feature);
          return;
        }
      }
      // Drawing functionality
      const trailPlanner = this.state.insertTrailPlanner ? this.state.insertTrailPlanner : this.state.trailPlanner;
      trailPlanner.routeToPoint(e.lngLat.toArray(), true);
      // deselect currently selected node if exists
      if (this.state.selectedRouteNode) {
        this.toggleSelectRouteNode(this.state.selectedRouteNode)
      }
    }
  },
  mapShiftDragStart(e) {
    if (this.state.insertTrailPlanner && !this.state.routingActive) {
      this.state.insertTrailPlanner.startDynamicPolyline(e.lngLat.toArray());
    } else if (this.state.trailPlanner && !this.state.routingActive) {
      this.state.trailPlanner.startDynamicPolyline(e.lngLat.toArray());
    }
  },
  mapShiftDragMove(e) {
    if (this.state.insertTrailPlanner && !this.state.routingActive) {
      this.state.insertTrailPlanner.updateDynamicPolyline(e.lngLat.toArray());
    } else if (this.state.trailPlanner && !this.state.routingActive) {
      this.state.trailPlanner.updateDynamicPolyline(e.lngLat.toArray());
    }
  },
  mapShiftDragEnd(e) {
    if (this.state.insertTrailPlanner && !this.state.routingActive) {
      this.state.insertTrailPlanner.endDynamicPolyline(e.lngLat.toArray());
    } else if (this.state.trailPlanner && !this.state.routingActive) {
      this.state.trailPlanner.endDynamicPolyline(e.lngLat.toArray());
    }
  },
  mapCtrlKey(pressed) {
    if (pressed) {
      this.toggleRouting();
    }
  },
  mapDelKey(pressed) {
    if (this.state.trailPlanner && this.state.selectedRouteNode && pressed) {
      this.state.trailPlanner.routeNodeRemoved(this.state.selectedRouteNode);
      this.toggleSelectRouteNode(this.state.selectedRouteNode);
    }
  },
  mapEscKey(pressed) {
    if (pressed) {
      this.disableInsertMode();
    }
  },
  toggleSelectRouteNode(routeNode) {
    if (this.state.selectedRouteNode && this.state.selectedRouteNode.id === routeNode.id) {
      // Deselect
      updateLayerProperty(this.mbMap, routeNodesId, 'layout', 'icon-size', 1);
      this.setState({ selectedRouteNode: null });
    } else {
      // Select
      const iconSize = ['match', ['get', 'id'], routeNode.id, 1.25, 1];
      updateLayerProperty(this.mbMap, routeNodesId, 'layout', 'icon-size', iconSize);
      this.setState({ selectedRouteNode: routeNode });
    }
    // need to know if there is a selected route node in parent components in order to enable/disable insert button
    this.props.handleRouteInfoChanged({ selectedNode: this.state.selectedRouteNode });
  },
  toggleRouting() {
    const routingActive = !this.state.routingActive;
    this.setState({ routingActive });
    if (this.state.trailPlanner) {
      this.state.trailPlanner.routingActive = routingActive;
    }
    if (this.state.insertTrailPlanner) {
      this.state.insertTrailPlanner.routingActive = routingActive;
    }
  },
  toggleRoutingTip(tipType, e) {
    e.stopPropagation();
    const toolTipState = { ...this.state.showRoutingTips };
    toolTipState[tipType] = false;
    this.setState({ showRoutingTips: toolTipState });
  },
  setRoutingMode(value) {
    const routingMode = value;
    this.setState({ routingMode });
    if (this.state.trailPlanner) {
      this.state.trailPlanner.routingMode = routingMode;
    }
    if (this.state.insertTrailPlanner) {
      this.state.insertTrailPlanner.routingMode = routingMode;
    }
  },
  outAndBackClick() {
    if (this.state.insertTrailPlanner) {
      this.state.insertTrailPlanner.outAndBack();
    } else if (this.state.trailPlanner) {
      this.state.trailPlanner.outAndBack();
    }
  },
  returnClick() {
    if (this.state.insertTrailPlanner) {
      this.state.insertTrailPlanner.returnToStart();
    } else if (this.state.trailPlanner) {
      this.state.trailPlanner.returnToStart();
    }
  },
  reverseClick() {
    if (this.state.trailPlanner) {
      this.state.trailPlanner.reverseRoute();
    }
  },
  undoClick() {
    if (this.state.insertTrailPlanner) {
      if (this.state.insertTrailPlanner.routePolylines.length < 1) {
        this.disableInsertMode();
      } else {
        this.state.insertTrailPlanner.undo();
      }
    } else if (this.state.trailPlanner) {
      this.state.trailPlanner.undo();
    }
  },
  redoClick() {
    if (this.state.insertTrailPlanner) {
      this.state.insertTrailPlanner.redo();
    } else if (this.state.trailPlanner) {
      this.state.trailPlanner.redo();
    }
  }
};

export { TrailPlannerMixin };
